import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  Plus, X, Fingerprint, MessageSquare, Sparkles, Bell, Bus,
  ClipboardCheck, Loader2, LogOut,
} from "lucide-react";
import { toast } from "sonner";

// Roles that can do geofenced check-in (staff with employee record)
const CHECKIN_ROLES = ["school_admin", "academic", "finance", "teacher", "hr", "driver"];

export default function QuickActionsFAB() {
  const { t, dir } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [todayRec, setTodayRec] = useState(null); // today's hr_attendance record

  const canCheckin = user && CHECKIN_ROLES.includes(user.role);

  // Fetch today's attendance status once so we know to show check-in or check-out
  const refreshToday = async () => {
    if (!canCheckin) return;
    try {
      const { data } = await api.get("/hr/me");
      setTodayRec(data?.today || null);
    } catch { /* silent — non-employee users get 404 or null */ }
  };
  useEffect(() => { refreshToday(); /* eslint-disable-next-line */ }, [user?.id]);

  if (!user) return null;

  const hasCheckedIn = !!(todayRec && todayRec.check_in);
  const hasCheckedOut = !!(todayRec && todayRec.check_out);

  const doGeoAction = (endpoint, successMsg, id) => new Promise((resolve) => {
    if (!navigator.geolocation) {
      toast.error(t("geolocation_unsupported"));
      resolve(null); return;
    }
    setBusy(true);
    toast.loading(t("getting_location"), { id });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude, accuracy } = pos.coords;
          const { data } = await api.post(endpoint, { lat: latitude, lng: longitude, accuracy });
          toast.success(successMsg(data), { id });
          setOpen(false);
          await refreshToday();
          resolve(data);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Error", { id });
          resolve(null);
        } finally { setBusy(false); }
      },
      (err) => {
        toast.error(err.code === 1 ? t("geolocation_denied") : (err.message || "Error"), { id });
        setBusy(false); resolve(null);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

  const handleCheckin = () => doGeoAction(
    "/hr/check-in",
    (data) => `${t("checkin_success")} · ${data.check_in}${data.late_min ? ` · ${t("late_min")} ${data.late_min} ${t("minutes")}` : ""}`,
    "fab-checkin",
  );

  const handleCheckout = () => doGeoAction(
    "/hr/check-out",
    (data) => `${t("checkout_success") || "تم تسجيل الخروج"} · ${data.check_out}`,
    "fab-checkout",
  );

  // Build role-aware quick action list
  const actions = [];
  if (canCheckin) {
    if (!hasCheckedIn) {
      actions.push({
        key: "checkin", icon: Fingerprint, label: t("checkin_now"),
        color: "from-emerald-500 to-emerald-600", onClick: handleCheckin,
      });
    } else if (!hasCheckedOut) {
      actions.push({
        key: "checkout", icon: LogOut, label: t("checkout_now") || "تسجيل الخروج الآن",
        color: "from-rose-500 to-rose-600", onClick: handleCheckout,
      });
    } else {
      // Already checked out — show a passive indicator (no click)
      actions.push({
        key: "done", icon: ClipboardCheck, label: `${t("checked_out") || "خروج"} · ${todayRec.check_out}`,
        color: "from-slate-500 to-slate-600", onClick: () => toast.info(t("attendance_recorded") || "تم تسجيل الحضور والانصراف اليوم"),
      });
    }
  }
  if (user.role === "driver") {
    actions.push({ key: "my-route", icon: Bus, label: t("my_route"), color: "from-blue-500 to-blue-600", to: "/my-route" });
  }
  if (["teacher", "academic", "school_admin", "super_admin"].includes(user.role)) {
    actions.push({ key: "attendance", icon: ClipboardCheck, label: t("rollCall"), color: "from-amber-500 to-orange-600", to: "/attendance" });
    actions.push({ key: "ai", icon: Sparkles, label: t("ai_assistant"), color: "from-violet-500 to-fuchsia-600", to: "/ai" });
  }
  if (["parent", "student"].includes(user.role)) {
    actions.push({ key: "msgs", icon: MessageSquare, label: t("messages"), color: "from-blue-500 to-indigo-600", to: "/messages" });
  }
  actions.push({ key: "notif", icon: Bell, label: t("notifications"), color: "from-rose-500 to-pink-600", to: "/notifications" });

  if (actions.length === 0) return null;

  const sideClass = dir === "rtl" ? "left-5" : "right-5";

  return (
    <div className={`fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-5 ${sideClass} z-40 flex flex-col items-end gap-3`} data-testid="quick-fab-root">
      {/* Expanded panel */}
      {open && (
        <div className="flex flex-col gap-2.5 mb-1 animate-fade-up">
          {actions.map((a) => (
            <Button
              key={a.key}
              type="button"
              disabled={busy}
              onClick={() => {
                if (a.onClick) { a.onClick(); }
                else if (a.to) { setOpen(false); navigate(a.to); }
              }}
              data-testid={`fab-${a.key}`}
              className={`bg-gradient-to-br ${a.color} text-white shadow-lg hover:shadow-xl hover:scale-105 smooth border-0 h-12 ps-4 pe-5 rounded-full justify-start`}
            >
              {busy && (a.key === "checkin" || a.key === "checkout") ? (
                <Loader2 className="w-4 h-4 me-2 animate-spin" />
              ) : (
                <a.icon className="w-4 h-4 me-2" />
              )}
              <span className="text-sm font-medium">{a.label}</span>
            </Button>
          ))}
        </div>
      )}

      {/* Trigger FAB */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="quick-fab-toggle"
        aria-label={t("quick_actions")}
        className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-2xl grid place-items-center hover:scale-110 smooth ring-4 ring-primary/20"
      >
        {open ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" strokeWidth={2.5} />}
      </button>
    </div>
  );
}
